import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SeasonWinnerData } from "../../entry/server/game.ts";
import { gameRepo } from "../../repo/game-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import PaginationView from "../../componrnts/pagination.tsx";
import { Pagination } from "../../lib/api.ts";

export default function SeasonWinnersList() {
  const [winners, setWinners] = useState<SeasonWinnerData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [seasonInput, setSeasonInput] = useState("");
  const [seasonFilter, setSeasonFilter] = useState<number | undefined>(undefined);
  const { setMessage } = useContext(AppContext);

  useEffect(() => {
    const fetchWinners = async () => {
      setLoading(true);
      try {
        const response = await gameRepo.getSeasonWinners(page, seasonFilter);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت لیست برندگان ناموفق بود." });
          setWinners([]);
          return;
        }
        setWinners(response.data);
        setPagination(response.pagination ?? null);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        setWinners([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchWinners();
  }, [page, seasonFilter, setMessage]);

  const onFilter = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = seasonInput.trim();
    if (!trimmed) {
      setSeasonFilter(undefined);
      setPage(1);
      return;
    }
    const season = Number(trimmed);
    if (!Number.isInteger(season) || season <= 0) {
      setMessage({ type: "error", message: "شماره فصل باید عدد بزرگ‌تر از صفر باشد." });
      return;
    }
    setSeasonFilter(season);
    setPage(1);
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("این برنده حذف شود؟")) return;

    try {
      const response = await gameRepo.deleteSeasonWinner(id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف برنده ناموفق بود." });
        return;
      }
      setWinners((prev) => prev.filter((item) => item.id !== id));
      setMessage({ type: "success", message: "برنده با موفقیت حذف شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title="برندگان" />
        <Link
          to="/admin/season-winners/new"
          className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400"
        >
          ثبت برنده
        </Link>
      </div>

      <form onSubmit={onFilter} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="w-full space-y-2 sm:max-w-xs">
          <span className="text-sm text-slate-300">فیلتر فصل</span>
          <input
            type="number"
            min={1}
            value={seasonInput}
            onChange={(event) => setSeasonInput(event.target.value)}
            placeholder="همه فصل‌ها"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
          />
        </label>
        <button
          type="submit"
          className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400"
        >
          اعمال
        </button>
      </form>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست برندگان...
        </div>
      ) : !winners.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">برنده‌ای یافت نشد.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">فصل</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">رتبه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">سطح</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">امتیاز</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">جایزه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {winners.map((winner) => (
                  <tr key={winner.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{winner.season_number}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{winner.rank}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-100">
                      {winner.username || `#${winner.user_id}`}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{winner.level}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{winner.score}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-slate-300" title={winner.prize}>
                      {winner.prize || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/admin/season-winners/${winner.id}/edit`}
                          className="inline-flex rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500"
                        >
                          ویرایش
                        </Link>
                        <button
                          type="button"
                          onClick={() => void onDelete(winner.id)}
                          className="inline-flex rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
